import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { LoginPageStyles } from './LoginPage.styles';
import { AuthWaves, LockIcon } from './AuthParts';
import { FormGroup, Label } from '../common/formInputsV2/styles/common';
import { PrimaryButton } from '../common/formInputsV2/Button.styles';

import GreeniaLogo from '../assets/images/greenia-logo.svg';
import { requestResetPassword } from '../features/auth/authActions';
import { generalAlertSuccess } from '../common/utils/alerts';

const schema = yup.object({
  password: yup
    .string()
    .required('Il campo è obbligatorio')
    .min(6, 'La password deve contenere almeno 6 caratteri')
    .max(60, 'La password deve contenere un massimo di 60 caratteri'),
  password_confirmation: yup.string()
    .required('Il campo è obbligatorio')
    .oneOf([yup.ref('password'), ''], 'Le password non coincidono'),
});

export function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const userState = useSelector((store) => store.user);
  const resetPasswordState = useSelector((store) => store.auth.resetPassword);

  useEffect(() => {
    if (userState.token) {
      navigate('/');
    }
  }, [userState.token]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  const submitData = ({ password }) => {
    requestResetPassword(token, { password }, () => {
      generalAlertSuccess('La password è stata cambiata');
      navigate('/login');
    });
  };

  return (
    <LoginPageStyles>
      <AuthWaves />

      <form onSubmit={handleSubmit(submitData)} className="login-card" noValidate>
        <div className="login-logo">
          <img src={GreeniaLogo} width={170} alt="Greenia Logo" />
        </div>
        <h1 className="login-title">Cambia la password</h1>
        <p className="login-subtitle">Scegli una nuova password per il tuo account</p>

        {resetPasswordState.error !== null && (
          <div className="login-error" role="alert">{resetPasswordState.error}</div>
        )}

        <FormGroup className="login-group">
          <Label htmlFor="password">Password</Label>
          <div className="login-field">
            <LockIcon />
            <input
              {...register('password')}
              id="password"
              type="password"
              autoComplete="new-password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              aria-invalid={errors.password ? 'true' : undefined}
              aria-describedby={errors.password ? 'password-error' : undefined}
            />
            <div className="invalid-feedback" id="password-error">{errors.password?.message}</div>
          </div>
        </FormGroup>

        <FormGroup className="login-group">
          <Label htmlFor="password_confirmation">Conferma la password</Label>
          <div className="login-field">
            <LockIcon />
            <input
              {...register('password_confirmation')}
              id="password_confirmation"
              type="password"
              autoComplete="new-password"
              className={`form-control ${errors.password_confirmation ? 'is-invalid' : ''}`}
              aria-invalid={errors.password_confirmation ? 'true' : undefined}
              aria-describedby={errors.password_confirmation ? 'password-confirmation-error' : undefined}
            />
            <div className="invalid-feedback" id="password-confirmation-error">
              {errors.password_confirmation?.message}
            </div>
          </div>
        </FormGroup>

        <div className="login-submit">
          <PrimaryButton type="submit" disabled={resetPasswordState.loading}>
            {resetPasswordState.loading ? 'Attendere prego...' : 'Imposta'}
          </PrimaryButton>
        </div>
        <p className="login-forgot">
          <Link to="/auth/forgot">Hai dimenticato la password?</Link>
        </p>
      </form>
    </LoginPageStyles>
  );
}
